import React, { useState, useEffect, useCallback } from 'react';
import { formatDate } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, Package, Truck, AlertTriangle, ArrowUpCircle, ArrowDownCircle,
  Trash2, Edit, Clock, Building2, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoriaLabels = {
  insumo: 'Insumo', embalagem: 'Embalagem', quimico: 'Químico',
  epi: 'EPI', material_escritorio: 'Mat. Escritório', outros: 'Outros'
};

const unidadeLabels = {
  un: 'Unidade', cx: 'Caixa', pct: 'Pacote', lt: 'Litro', kg: 'Kg', m: 'Metro', rl: 'Rolo'
};

export default function EstoquePage() {
  const [activeTab, setActiveTab] = useState('produtos');
  const [loading, setLoading] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showProdutoDialog, setShowProdutoDialog] = useState(false);
  const [showFornecedorDialog, setShowFornecedorDialog] = useState(false);
  const [showMovimentacaoDialog, setShowMovimentacaoDialog] = useState(false);
  const [showCompraDialog, setShowCompraDialog] = useState(false);
  
  const [produtoForm, setProdutoForm] = useState({
    nome: '', categoria: 'insumo', unidade_medida: 'un', quantidade_minima: 0,
    localizacao: '', fornecedor_id: '', custo_unitario: 0, observacoes: ''
  });
  
  const [fornecedorForm, setFornecedorForm] = useState({
    razao_social: '', nome_fantasia: '', cnpj: '', telefone: '', email: '', endereco: '', contato_nome: ''
  });
  
  const [movimentacaoForm, setMovimentacaoForm] = useState({
    produto_id: '', tipo: 'saida', quantidade: 1, motivo: '', observacoes: ''
  });
  
  const [compraForm, setCompraForm] = useState({
    fornecedor_id: '', numero_nota_fiscal: '', data_compra: new Date().toISOString().split('T')[0],
    itens: [], valor_frete: 0, valor_desconto: 0
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, fornRes, loteRes, movRes, alertaRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/estoque/produtos`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/estoque/fornecedores`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/estoque/lotes`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/estoque/movimentacoes`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/estoque/alertas`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/estoque/estatisticas`, { headers: getAuthHeaders() })
      ]);
      if (prodRes.ok) setProdutos(await prodRes.json());
      if (fornRes.ok) setFornecedores(await fornRes.json());
      if (loteRes.ok) setLotes(await loteRes.json());
      if (movRes.ok) setMovimentacoes(await movRes.json());
      if (alertaRes.ok) setAlertas(await alertaRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduto = async () => {
    if (!produtoForm.nome) { toast.error('Nome é obrigatório'); return; }
    try {
      const res = await fetch(`${API_URL}/api/estoque/produtos`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(produtoForm)
      });
      if (res.ok) {
        toast.success('Produto criado'); setShowProdutoDialog(false);
        setProdutoForm({ nome: '', categoria: 'insumo', unidade_medida: 'un', quantidade_minima: 0, localizacao: '', fornecedor_id: '', custo_unitario: 0, observacoes: '' });
        fetchData();
      } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
    } catch { toast.error('Erro ao criar produto'); }
  };

  const handleCreateFornecedor = async () => {
    if (!fornecedorForm.razao_social) { toast.error('Razão Social é obrigatória'); return; }
    try {
      const res = await fetch(`${API_URL}/api/estoque/fornecedores`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(fornecedorForm)
      });
      if (res.ok) {
        toast.success('Fornecedor criado'); setShowFornecedorDialog(false);
        setFornecedorForm({ razao_social: '', nome_fantasia: '', cnpj: '', telefone: '', email: '', endereco: '', contato_nome: '' });
        fetchData();
      } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
    } catch { toast.error('Erro ao criar fornecedor'); }
  };

  const handleCreateMovimentacao = async () => {
    if (!movimentacaoForm.produto_id || movimentacaoForm.quantidade <= 0) { toast.error('Selecione produto e quantidade'); return; }
    try {
      const res = await fetch(`${API_URL}/api/estoque/movimentacoes`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(movimentacaoForm)
      });
      if (res.ok) {
        toast.success(`${movimentacaoForm.tipo === 'entrada' ? 'Entrada' : 'Saída'} registrada`);
        setShowMovimentacaoDialog(false);
        setMovimentacaoForm({ produto_id: '', tipo: 'saida', quantidade: 1, motivo: '', observacoes: '' });
        fetchData();
      } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
    } catch { toast.error('Erro'); }
  };

  const handleRegistrarCompra = async () => {
    if (!compraForm.fornecedor_id || compraForm.itens.length === 0) { toast.error('Fornecedor e itens são obrigatórios'); return; }
    try {
      const res = await fetch(`${API_URL}/api/estoque/compras`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(compraForm)
      });
      if (res.ok) {
        toast.success('Compra registrada'); setShowCompraDialog(false);
        setCompraForm({ fornecedor_id: '', numero_nota_fiscal: '', data_compra: new Date().toISOString().split('T')[0], itens: [], valor_frete: 0, valor_desconto: 0 });
        fetchData();
      } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
    } catch { toast.error('Erro'); }
  };

  const addItemCompra = () => {
    setCompraForm(prev => ({ ...prev, itens: [...prev.itens, { produto_id: '', quantidade: 1, custo_unitario: 0, numero_lote: '', data_validade: '' }] }));
  };

  const updateItemCompra = (index, field, value) => {
    setCompraForm(prev => ({ ...prev, itens: prev.itens.map((item, i) => i === index ? { ...item, [field]: value } : item) }));
  };

  const removeItemCompra = (index) => {
    setCompraForm(prev => ({ ...prev, itens: prev.itens.filter((_, i) => i !== index) }));
  };

  const filteredProdutos = produtos.filter(p => p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) || p.codigo?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredFornecedores = fornecedores.filter(f => f.razao_social?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="estoque-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Controle de Estoque</h1>
          <p className="text-slate-500">Produtos, lotes, movimentações e fornecedores</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 w-64" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" />
              <div><p className="text-sm text-blue-600">Produtos</p><p className="text-2xl font-bold text-blue-900">{stats.total_produtos || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-green-600" />
              <div><p className="text-sm text-green-600">Fornecedores</p><p className="text-2xl font-bold text-green-900">{stats.total_fornecedores || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div><p className="text-sm text-red-600">Estoque Baixo</p><p className="text-2xl font-bold text-red-900">{stats.produtos_estoque_baixo || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-600" />
              <div><p className="text-sm text-amber-600">Vencendo</p><p className="text-2xl font-bold text-amber-900">{stats.lotes_vencendo_30_dias || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-purple-600" />
              <div><p className="text-sm text-purple-600">Valor Estoque</p><p className="text-lg font-bold text-purple-900">R$ {(stats.valor_total_estoque || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Alertas ({alertas.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertas.slice(0, 5).map((alerta, i) => (
                <div key={i} className={`flex items-center gap-3 p-2 rounded ${alerta.urgencia === 'alta' ? 'bg-red-100' : alerta.urgencia === 'media' ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <Badge className={alerta.urgencia === 'alta' ? 'bg-red-600' : alerta.urgencia === 'media' ? 'bg-amber-600' : 'bg-gray-600'}>{alerta.urgencia.toUpperCase()}</Badge>
                  <span className="font-medium">{alerta.produto_nome}</span>
                  <span className="text-slate-600">{alerta.mensagem}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="produtos" data-testid="tab-produtos"><Package className="w-4 h-4 mr-2" />Produtos</TabsTrigger>
          <TabsTrigger value="movimentacoes" data-testid="tab-movimentacoes"><ArrowUpCircle className="w-4 h-4 mr-2" />Movimentações</TabsTrigger>
          <TabsTrigger value="lotes" data-testid="tab-lotes"><Clock className="w-4 h-4 mr-2" />Lotes</TabsTrigger>
          <TabsTrigger value="fornecedores" data-testid="tab-fornecedores"><Truck className="w-4 h-4 mr-2" />Fornecedores</TabsTrigger>
        </TabsList>

        {/* PRODUTOS */}
        <TabsContent value="produtos" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCompraDialog(true)} data-testid="btn-registrar-compra">
              <ArrowUpCircle className="w-4 h-4 mr-2 text-green-600" />Registrar Compra
            </Button>
            <Button onClick={() => setShowProdutoDialog(true)} data-testid="btn-novo-produto"><Plus className="w-4 h-4 mr-2" />Novo Produto</Button>
          </div>
          <div className="grid gap-3">
            {filteredProdutos.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500"><Package className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Nenhum produto cadastrado</p></CardContent></Card>
            ) : (
              filteredProdutos.map(p => (
                <Card key={p.id} data-testid={`produto-${p.id}`} className={`border-l-4 ${p.estoque_baixo ? 'border-l-red-500' : 'border-l-green-500'}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${p.estoque_baixo ? 'bg-red-100' : 'bg-green-100'}`}>
                          <Package className={`w-5 h-5 ${p.estoque_baixo ? 'text-red-600' : 'text-green-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{p.nome}</p>
                            <Badge variant="outline" className="text-xs">{p.codigo}</Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Badge className="text-xs">{categoriaLabels[p.categoria] || p.categoria}</Badge>
                            <span>•</span>
                            <span>{p.fornecedor_nome || 'Sem fornecedor'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-right">
                        <div>
                          <p className="text-sm text-slate-500">Estoque</p>
                          <p className={`text-lg font-bold ${p.estoque_baixo ? 'text-red-600' : ''}`}>{p.quantidade_atual || 0} {unidadeLabels[p.unidade_medida] || p.unidade_medida}</p>
                          {p.estoque_baixo && <p className="text-xs text-red-500">Mín: {p.quantidade_minima}</p>}
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Custo</p>
                          <p className="font-semibold">R$ {(p.custo_unitario || 0).toFixed(2)}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => { setMovimentacaoForm(prev => ({ ...prev, produto_id: p.id })); setShowMovimentacaoDialog(true); }}>
                          <ArrowDownCircle className="w-4 h-4 mr-1" />Dar Baixa
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* MOVIMENTAÇÕES */}
        <TabsContent value="movimentacoes" className="space-y-4">
          <div className="grid gap-3">
            {movimentacoes.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500">Nenhuma movimentação</CardContent></Card>
            ) : (
              movimentacoes.slice(0, 20).map(m => (
                <Card key={m.id} className={`border-l-4 ${m.tipo === 'entrada' ? 'border-l-green-500' : 'border-l-red-500'}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {m.tipo === 'entrada' ? <ArrowUpCircle className="w-5 h-5 text-green-600" /> : <ArrowDownCircle className="w-5 h-5 text-red-600" />}
                        <div>
                          <p className="font-medium">{m.produto_nome}</p>
                          <p className="text-sm text-slate-500">{m.motivo || m.tipo} • {formatDate(m.data_movimentacao)} • {m.usuario_nome}</p>
                        </div>
                      </div>
                      <Badge className={m.tipo === 'entrada' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{m.tipo === 'entrada' ? '+' : '-'}{m.quantidade}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* LOTES */}
        <TabsContent value="lotes" className="space-y-4">
          <div className="grid gap-3">
            {lotes.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500">Nenhum lote com estoque</CardContent></Card>
            ) : (
              lotes.map(l => (
                <Card key={l.id} className={l.dias_para_vencer !== null && l.dias_para_vencer <= 30 ? 'border-amber-300' : ''}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{l.produto_nome}</p>
                        <p className="text-sm text-slate-500">Lote: {l.numero_lote}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold">{l.quantidade} {unidadeLabels[l.unidade_medida] || 'un'}</p>
                        </div>
                        {l.data_validade && (
                          <Badge className={l.dias_para_vencer <= 7 ? 'bg-red-100 text-red-800' : l.dias_para_vencer <= 30 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                            {l.dias_para_vencer <= 0 ? 'VENCIDO' : `Vence em ${l.dias_para_vencer}d`}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* FORNECEDORES */}
        <TabsContent value="fornecedores" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowFornecedorDialog(true)} data-testid="btn-novo-fornecedor"><Plus className="w-4 h-4 mr-2" />Novo Fornecedor</Button>
          </div>
          <div className="grid gap-3">
            {filteredFornecedores.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500">Nenhum fornecedor</CardContent></Card>
            ) : (
              filteredFornecedores.map(f => (
                <Card key={f.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Truck className="w-8 h-8 text-slate-400" />
                        <div>
                          <p className="font-medium">{f.razao_social}</p>
                          <p className="text-sm text-slate-500">{f.nome_fantasia || ''} • {f.cnpj || 'Sem CNPJ'}</p>
                        </div>
                      </div>
                      <div className="text-sm text-slate-500">{f.telefone} • {f.email}</div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* DIALOGS */}
      <Dialog open={showProdutoDialog} onOpenChange={setShowProdutoDialog}>
        <DialogContent><form onSubmit={(e) => { e.preventDefault(); handleCreateProduto(); }}>
          <DialogHeader><DialogTitle>Novo Produto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Nome *</Label><Input value={produtoForm.nome} onChange={(e) => setProdutoForm(p => ({ ...p, nome: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Categoria</Label><Select value={produtoForm.categoria || undefined} onValueChange={(v) => setProdutoForm(p => ({ ...p, categoria: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(categoriaLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Unidade</Label><Select value={produtoForm.unidade_medida || undefined} onValueChange={(v) => setProdutoForm(p => ({ ...p, unidade_medida: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(unidadeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Qtd Mínima</Label><Input type="number" onFocus={(e) => e.target.select()} value={produtoForm.quantidade_minima} onChange={(e) => setProdutoForm(p => ({ ...p, quantidade_minima: parseFloat(e.target.value) || 0 }))} /></div>
              <div><Label>Custo Unit.</Label><Input type="number" onFocus={(e) => e.target.select()} step="0.01" value={produtoForm.custo_unitario} onChange={(e) => setProdutoForm(p => ({ ...p, custo_unitario: parseFloat(e.target.value) || 0 }))} /></div>
            </div>
            <div><Label>Fornecedor</Label><Select value={produtoForm.fornecedor_id || "none"} onValueChange={(v) => setProdutoForm(p => ({ ...p, fornecedor_id: v === "none" ? "" : v }))}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent><SelectItem value="none">Nenhum</SelectItem>{fornecedores.map(f => <SelectItem key={f.id} value={f.id}>{f.razao_social}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setShowProdutoDialog(false)}>Cancelar</Button><Button type="submit">Criar</Button></DialogFooter>
        </form></DialogContent>
      </Dialog>

      <Dialog open={showFornecedorDialog} onOpenChange={setShowFornecedorDialog}>
        <DialogContent><form onSubmit={(e) => { e.preventDefault(); handleCreateFornecedor(); }}>
          <DialogHeader><DialogTitle>Novo Fornecedor</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Razão Social *</Label><Input value={fornecedorForm.razao_social} onChange={(e) => setFornecedorForm(f => ({ ...f, razao_social: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nome Fantasia</Label><Input value={fornecedorForm.nome_fantasia} onChange={(e) => setFornecedorForm(f => ({ ...f, nome_fantasia: e.target.value }))} /></div>
              <div><Label>CNPJ</Label><Input value={fornecedorForm.cnpj} onChange={(e) => setFornecedorForm(f => ({ ...f, cnpj: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Telefone</Label><Input value={fornecedorForm.telefone} onChange={(e) => setFornecedorForm(f => ({ ...f, telefone: e.target.value }))} /></div>
              <div><Label>Email</Label><Input value={fornecedorForm.email} onChange={(e) => setFornecedorForm(f => ({ ...f, email: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setShowFornecedorDialog(false)}>Cancelar</Button><Button type="submit">Criar</Button></DialogFooter>
        </form></DialogContent>
      </Dialog>

      <Dialog open={showMovimentacaoDialog} onOpenChange={setShowMovimentacaoDialog}>
        <DialogContent><form onSubmit={(e) => { e.preventDefault(); handleCreateMovimentacao(); }}>
          <DialogHeader><DialogTitle>{movimentacaoForm.tipo === 'entrada' ? 'Entrada' : 'Saída'} de Estoque</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Produto *</Label><Select value={movimentacaoForm.produto_id || undefined} onValueChange={(v) => setMovimentacaoForm(m => ({ ...m, produto_id: v }))}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{produtos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} ({p.quantidade_atual || 0} em estoque)</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tipo</Label><Select value={movimentacaoForm.tipo || undefined} onValueChange={(v) => setMovimentacaoForm(m => ({ ...m, tipo: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="entrada">Entrada</SelectItem><SelectItem value="saida">Saída</SelectItem></SelectContent></Select></div>
              <div><Label>Quantidade *</Label><Input type="number" onFocus={(e) => e.target.select()} min="1" value={movimentacaoForm.quantidade} onChange={(e) => setMovimentacaoForm(m => ({ ...m, quantidade: parseFloat(e.target.value) || 1 }))} /></div>
            </div>
            <div><Label>Motivo</Label><Input value={movimentacaoForm.motivo} onChange={(e) => setMovimentacaoForm(m => ({ ...m, motivo: e.target.value }))} placeholder="Ex: Consumo, Ajuste..." /></div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setShowMovimentacaoDialog(false)}>Cancelar</Button><Button type="submit" className={movimentacaoForm.tipo === 'entrada' ? 'bg-green-600' : 'bg-red-600'}>{movimentacaoForm.tipo === 'entrada' ? 'Registrar Entrada' : 'Registrar Saída'}</Button></DialogFooter>
        </form></DialogContent>
      </Dialog>

      <Dialog open={showCompraDialog} onOpenChange={setShowCompraDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><form onSubmit={(e) => { e.preventDefault(); handleRegistrarCompra(); }}>
          <DialogHeader><DialogTitle>Registrar Compra</DialogTitle><DialogDescription>Entrada de produtos com nota fiscal</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fornecedor *</Label><Select value={compraForm.fornecedor_id || undefined} onValueChange={(v) => setCompraForm(c => ({ ...c, fornecedor_id: v }))}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.id}>{f.razao_social}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Nº Nota Fiscal</Label><Input value={compraForm.numero_nota_fiscal} onChange={(e) => setCompraForm(c => ({ ...c, numero_nota_fiscal: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Data Compra</Label><Input type="date" value={compraForm.data_compra} onChange={(e) => setCompraForm(c => ({ ...c, data_compra: e.target.value }))} /></div>
              <div><Label>Frete</Label><Input type="number" onFocus={(e) => e.target.select()} step="0.01" value={compraForm.valor_frete} onChange={(e) => setCompraForm(c => ({ ...c, valor_frete: parseFloat(e.target.value) || 0 }))} /></div>
              <div><Label>Desconto</Label><Input type="number" onFocus={(e) => e.target.select()} step="0.01" value={compraForm.valor_desconto} onChange={(e) => setCompraForm(c => ({ ...c, valor_desconto: parseFloat(e.target.value) || 0 }))} /></div>
            </div>
            <div>
              <div className="flex justify-between mb-2"><Label>Itens *</Label><Button type="button" size="sm" variant="outline" onClick={addItemCompra}><Plus className="w-4 h-4 mr-1" />Item</Button></div>
              {compraForm.itens.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2 items-end">
                  <div className="flex-1"><Select value={item.produto_id || undefined} onValueChange={(v) => updateItemCompra(i, 'produto_id', v)}><SelectTrigger><SelectValue placeholder="Produto" /></SelectTrigger><SelectContent>{produtos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent></Select></div>
                  <div className="w-20"><Input type="number" onFocus={(e) => e.target.select()} min="1" value={item.quantidade} onChange={(e) => updateItemCompra(i, 'quantidade', parseFloat(e.target.value) || 1)} placeholder="Qtd" /></div>
                  <div className="w-24"><Input type="number" onFocus={(e) => e.target.select()} step="0.01" value={item.custo_unitario} onChange={(e) => updateItemCompra(i, 'custo_unitario', parseFloat(e.target.value) || 0)} placeholder="Custo" /></div>
                  <div className="w-24"><Input value={item.numero_lote} onChange={(e) => updateItemCompra(i, 'numero_lote', e.target.value)} placeholder="Lote" /></div>
                  <div className="w-32"><Input type="date" value={item.data_validade} onChange={(e) => updateItemCompra(i, 'data_validade', e.target.value)} /></div>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeItemCompra(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
              {compraForm.itens.length > 0 && (
                <div className="text-right pt-2 border-t mt-2">
                  <span className="text-sm text-slate-500">Total: </span>
                  <span className="font-bold">R$ {(compraForm.itens.reduce((acc, i) => acc + i.quantidade * i.custo_unitario, 0) + compraForm.valor_frete - compraForm.valor_desconto).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setShowCompraDialog(false)}>Cancelar</Button><Button type="submit" className="bg-green-600">Registrar Compra</Button></DialogFooter>
        </form></DialogContent>
      </Dialog>
    </div>
  );
}
